import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

// Native THG identity sequence, translated from the supplied logo-motion reference:
// the shield emerges from darkness and illuminates, a thin red rule draws,
// TITAN HAVEN GROUP LLC resolves, then the overlay releases into the homepage hero.
// Plays once per session (~2.6s meaningful reveal + 0.8s release); click skips;
// reduced-motion visitors never see it. Uses the official shield artwork unaltered.
export const IntroReveal = () => {
    const reduce = useReducedMotion();
    const [phase, setPhase] = useState("idle"); // idle | playing | leaving | done

    useEffect(() => {
        if (reduce || sessionStorage.getItem("thg-intro-v2")) {
            setPhase("done");
            return;
        }
        setPhase("playing");
        // Flag is written when the sequence completes, not at start — StrictMode
        // double-invokes effects in dev, and writing it early would self-skip.
        const t1 = setTimeout(() => {
            sessionStorage.setItem("thg-intro-v2", "1");
            setPhase("leaving");
        }, 2600);
        const t2 = setTimeout(() => setPhase("done"), 3400);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [reduce]);

    if (phase === "idle" || phase === "done") return null;

    const finish = () => {
        sessionStorage.setItem("thg-intro-v2", "1");
        setPhase("leaving");
        setTimeout(() => setPhase("done"), 800);
    };

    return (
        <motion.div
            data-testid="intro-reveal"
            role="presentation"
            aria-hidden="true"
            onClick={finish}
            className="fixed inset-0 z-[100] bg-[#050505] flex flex-col items-center justify-center cursor-pointer"
            initial={{ opacity: 1 }}
            animate={{ opacity: phase === "leaving" ? 0 : 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            style={{ pointerEvents: phase === "leaving" ? "none" : "auto" }}
        >
            <motion.img
                src="/assets/thg-shield.png"
                alt=""
                className="w-24 md:w-32 h-auto"
                initial={{ opacity: 0, scale: 0.94, filter: "brightness(0.15)" }}
                animate={{ opacity: 1, scale: 1, filter: "brightness(1)" }}
                transition={{ duration: 1.5, ease: "easeOut" }}
            />
            <motion.div
                className="mt-7 h-px bg-thg-red"
                initial={{ width: 0 }}
                animate={{ width: 96 }}
                transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
            />
            <motion.p
                className="mt-7 font-display text-base md:text-xl tracking-[0.3em] text-white text-center px-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
            >
                TITAN HAVEN GROUP <span className="text-thg-secondary">LLC</span>
            </motion.p>
        </motion.div>
    );
};
